import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useUpdateEntryRule } from '../../api/useEntry';
import { EntryRule } from '../../types/entry.types';

interface Props {
  open: boolean;
  onClose: () => void;
  rule: EntryRule;
}

/**
 * Compact editor for an entry rule's out-of-session hedge-check safety net.
 * The opener page is an inline-cell table with no per-rule settings form, so
 * this small dialog owns the four hedge-check fields and saves them via
 * `useUpdateEntryRule`. Mirrors the unwinder's CreateRuleDialog section. The
 * actual detection runs in the always-on `manual-order-monitor` watchdog.
 */
export const HedgeCheckEditorDialog = ({ open, onClose, rule }: Props) => {
  const updateMutation = useUpdateEntryRule();

  const [enabled, setEnabled] = useState(false);
  const [intervalMin, setIntervalMin] = useState('5');
  const [maxUnhedged, setMaxUnhedged] = useState('');
  const [confirmations, setConfirmations] = useState('2');

  useEffect(() => {
    if (!open) return;
    setEnabled(!!rule.hedgeCheckEnabled);
    setIntervalMin(
      String(Math.round((rule.hedgeCheckIntervalMs ?? 300000) / 60000)),
    );
    setMaxUnhedged(
      rule.maxUnhedgedContracts != null
        ? String(rule.maxUnhedgedContracts)
        : '',
    );
    setConfirmations(String(rule.hedgeCheckConfirmations ?? 2));
  }, [open, rule]);

  const canSave = !enabled || Number(maxUnhedged) > 0;

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      id: rule._id,
      dto: {
        hedgeCheckEnabled: enabled,
        hedgeCheckIntervalMs: Math.max(1, Number(intervalMin)) * 60000,
        hedgeCheckConfirmations: Math.max(1, Number(confirmations)),
        ...(maxUnhedged
          ? { maxUnhedgedContracts: Number(maxUnhedged) }
          : {}),
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        Hedge safety check — {rule.pairName}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
            Periodically checks held positions against the exchange APIs. If the
            legs stay unhedged beyond the limit, the rule is stopped, orders
            cancelled, and you are notified.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Enable hedge check
              </Typography>
            }
          />
          {enabled && (
            <Stack spacing={1.5}>
              <TextField
                label="Max unhedged (contracts)"
                type="number"
                size="small"
                value={maxUnhedged}
                onChange={e => setMaxUnhedged(e.target.value)}
                inputProps={{ step: 1, min: 1 }}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Check every (minutes)"
                  type="number"
                  size="small"
                  value={intervalMin}
                  onChange={e => setIntervalMin(e.target.value)}
                  inputProps={{ step: 1, min: 1 }}
                  fullWidth
                />
                <TextField
                  label="Confirmations"
                  type="number"
                  size="small"
                  value={confirmations}
                  onChange={e => setConfirmations(e.target.value)}
                  inputProps={{ step: 1, min: 1 }}
                  fullWidth
                  helperText="Breaches before panic"
                />
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || updateMutation.isLoading}
          size="small"
        >
          {updateMutation.isLoading ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
